<template>
  <div class="products-showcase-slider relative">
    <template v-if="withArrows">
      <button
        v-show="canScrollLeft"
        type="button"
        class="products-showcase-slider__arrow products-showcase-slider__arrow--left hidden md:flex"
        :aria-label="t('general.pagination.previous')"
        data-test-id="products-showcase-slider-arrow-left"
        @click="scrollByDirection('left')"
      >
        <span
          class="products-showcase-slider__arrow-button flex items-center justify-center"
        >
          <ChevronLeft class="mr-0.5 text-black" />
        </span>
      </button>

      <button
        v-show="canScrollRight"
        type="button"
        class="products-showcase-slider__arrow products-showcase-slider__arrow--right hidden md:flex"
        :aria-label="t('general.pagination.next')"
        data-test-id="products-showcase-slider-arrow-right"
        @click="scrollByDirection('right')"
      >
        <span
          class="products-showcase-slider__arrow-button flex items-center justify-center"
        >
          <ChevronRight class="ml-0.5 text-black" />
        </span>
      </button>
    </template>

    <div
      ref="scrollContainerRef"
      v-dragscroll.x="enableDragScroll"
      v-bind="$attrs"
      @scroll.passive="updateScrollState"
      @dragscrollstart="onDragStart"
      @click.capture="onDragClick"
    >
      <slot />
    </div>
  </div>
</template>

<script setup lang="ts">
import { dragscroll } from 'vue-dragscroll'
import ChevronRight from '~/assets/icons/chevron-right.svg?component'
import ChevronLeft from '~/assets/icons/chevron-left.svg?component'

defineOptions({ inheritAttrs: false })

const vDragscroll = dragscroll

const DESKTOP_VIEWPORT = '(min-width: 768px)'
const SCROLL_EDGE_THRESHOLD = 50

/**
 * `withArrows` also gates drag-to-scroll: both only make sense when the
 * container keeps scrolling horizontally on desktop. When it collapses into a
 * grid at `md:` the arrows would point at nothing.
 */
const { withArrows = false } = defineProps<{
  withArrows?: boolean
}>()

const { t } = useI18n()

const scrollContainerRef = ref<HTMLElement | null>(null)
const enableDragScroll = ref(false)
const canScrollLeft = ref(false)
const canScrollRight = ref(false)
const dragged = ref(false)
const dragTimeout = ref<ReturnType<typeof setTimeout> | null>(null)

const updateScrollState = () => {
  const container = scrollContainerRef.value
  if (!container || !withArrows) return

  canScrollLeft.value = container.scrollLeft > SCROLL_EDGE_THRESHOLD
  canScrollRight.value =
    container.scrollLeft + container.offsetWidth <
    container.scrollWidth - SCROLL_EDGE_THRESHOLD
}

const scrollByDirection = (direction: 'left' | 'right') => {
  const container = scrollContainerRef.value
  if (!container) return

  const offset =
    direction === 'right' ? container.offsetWidth : -container.offsetWidth

  container.scrollTo({
    left: container.scrollLeft + offset,
    behavior: 'smooth',
  })
}

const onDragStart = () => {
  if (!enableDragScroll.value) return

  if (dragTimeout.value) {
    clearTimeout(dragTimeout.value)
  }

  dragged.value = false
  dragTimeout.value = setTimeout(() => {
    dragged.value = true
  }, 100)
}

// Swallow the click that ends a drag so dragging the rail never opens a card.
const onDragClick = (event: Event) => {
  if (!enableDragScroll.value || !dragged.value) return

  event.preventDefault()
  dragged.value = false
}

onMounted(() => {
  if (!withArrows) return

  const mediaQuery = window.matchMedia(DESKTOP_VIEWPORT)
  const syncDragScroll = () => {
    enableDragScroll.value = mediaQuery.matches
    void nextTick(() => updateScrollState())
  }

  syncDragScroll()
  mediaQuery.addEventListener('change', syncDragScroll)

  // Cards arrive asynchronously (Algolia) — recompute once the rail resizes.
  const resizeObserver = new ResizeObserver(() => updateScrollState())
  if (scrollContainerRef.value) {
    resizeObserver.observe(scrollContainerRef.value)
  }

  onBeforeUnmount(() => {
    mediaQuery.removeEventListener('change', syncDragScroll)
    resizeObserver.disconnect()
    if (dragTimeout.value) clearTimeout(dragTimeout.value)
  })
})
</script>

<style lang="postcss" scoped>
.products-showcase-slider__arrow {
  position: absolute;
  top: 0;
  z-index: 3;
  height: 69%;
  width: 50px;
  align-items: center;
  border: 0;
  padding: 0;
  cursor: pointer;
}

.products-showcase-slider__arrow--left {
  left: 0;
  justify-content: flex-start;
  background: linear-gradient(to right, var(--color-white), rgba(0, 0, 0, 0));
}

.products-showcase-slider__arrow--right {
  right: 0;
  justify-content: flex-end;
  background: linear-gradient(to right, rgba(0, 0, 0, 0), var(--color-white));
}

.products-showcase-slider__arrow-button {
  @apply w-10 h-10 rounded-full border border-gray-200 bg-white;
}
</style>
